/**
 * Pure, deterministic game simulation. No DOM, no timers, no globals.
 * createSim(config) -> state; step(state, input) -> events.
 */
import { DEFAULTS } from './defaults.ts';
import { rngNext } from './rng.ts';
import {
  collectPickup,
  digHardDirt,
  gemLocked,
  hasGoals,
  hitPillar,
  isHardDirt,
  loadObjectives,
  markLockedGemCollected,
  nextSpawnPoint,
  impossibleGoal,
  onCellDug,
  onCreatureCrushed,
  onGemCollected,
  onPlayerArrive,
  onPlayerDeath,
  pillarAt,
  updateObjectives,
} from './objectives.ts';
import {
  DX,
  DY,
  OPPOSITE,
  isHorizontal,
  type Board,
  type Creature,
  type Dir,
  type Goal,
  type InputFrame,
  type KitKind,
  type Player,
  type Sack,
  type SimConfig,
  type SimEvent,
  type SimState,
} from './types.ts';

export const NO_INPUT: InputFrame = { dir: 0, stop: false, fire: false };

// ---------------------------------------------------------------------------
// Creation

export function createSim(config: SimConfig): SimState {
  if (config.boards.length === 0) throw new Error('createSim: no boards');
  const board = config.boards[0];
  const state: SimState = {
    tick: 0,
    rng: config.seed >>> 0,
    phase: 'playing',
    phaseTimer: 0,
    level: 1,
    boardIndex: 0,
    boardId: board.id,
    width: board.width,
    height: board.height,
    dug: [],
    gems: [],
    gemTypes: [],
    hardDirt: [],
    gemsRemaining: 0,
    gemsTotal: 0,
    params: board.params,
    objectives: { goals: [], phaseCount: 0, phase: 0, phases: [], obstacles: [], regions: {}, pickups: [], inventory: {}, nestTurn: false },
    player: makePlayer(board.playerStart.x, board.playerStart.y),
    playerStart: { ...board.playerStart },
    spawn: { ...board.spawn },
    creatures: [],
    nextCreatureId: 1,
    sacks: [],
    nextSackId: 1,
    golds: [],
    flame: null,
    score: 0,
    lives: DEFAULTS.startingLives - 1,
    nextLifeAt: DEFAULTS.extraLifeEvery,
    chainCount: 0,
    chainTimer: 0,
    spawnTimer: 0,
    spawned: 0,
    killed: 0,
    total: 0,
    bonusItem: null,
    bonusTimer: 0,
    bonusEatValue: 0,
    bonusActive: false,
    levelsCleared: 0,
    playerTicksH: config.playerTicksPerCellH ?? DEFAULTS.playerTicksPerCellH,
    playerTicksV: config.playerTicksPerCellV ?? DEFAULTS.playerTicksPerCellV,
    lantern: { fill: 0, charges: 0, chargeFill: 0 },
    beam: null,
  };
  loadLevel(state, board, config, []);
  // Pre-level picks apply to the first level of the run (Systems Brief §2.2).
  for (const k of config.picks ?? []) applyKit(state, k, 'pick', []);
  return state;
}

function makePlayer(x: number, y: number): Player {
  return {
    x,
    y,
    dir: 0,
    facing: 2,
    moving: false,
    prog: 0,
    ticks: 1,
    queued: 0,
    stopping: false,
    queuedBoundaries: 0,
    alive: true,
    deathTimer: 0,
    deathCause: '',
    invincible: 0,
    digHold: 0,
    shield: 0,
    turbo: 0,
  };
}

function loadLevel(state: SimState, board: Board, config: SimConfig, events: SimEvent[]): void {
  state.boardId = board.id;
  state.width = board.width;
  state.height = board.height;
  state.dug = board.dug.slice();
  state.gems = board.gems.slice();
  state.gemTypes = board.gemTypes.slice();
  state.gemsRemaining = board.gems.reduce((a, b) => a + b, 0);
  state.gemsTotal = state.gemsRemaining;
  state.params = board.params;
  state.playerStart = { ...board.playerStart };
  state.spawn = { ...board.spawn };
  state.sacks = [...board.sacks.map((s) => ({ ...s, kind: 'sack' as const })), ...board.boulders.map((s) => ({ ...s, kind: 'boulder' as const }))].map((s) => ({
    id: state.nextSackId++,
    x: s.x,
    y: s.y,
    kind: s.kind,
    state: 'rest' as const,
    wobble: 0,
    fallProg: 0,
    rowsFallen: 0,
    slideFromX: s.x,
    slideT: 0,
    shadow: 0,
    shadowSpent: false,
  }));
  state.golds = [];
  state.flame = null;
  state.creatures = [];
  state.spawned = 0;
  state.killed = 0;
  state.total = board.params.totalCreatures;
  state.spawnTimer = board.params.spawnGapTicks;
  state.bonusItem = null;
  state.bonusTimer = 0;
  state.bonusActive = false;
  state.bonusEatValue = 0;
  state.chainCount = 0;
  state.chainTimer = 0;
  // Precedence: run config (settings) > board param > defaults.
  state.playerTicksH = config.playerTicksPerCellH ?? board.params.playerSpeedTicksPerCell.h;
  state.playerTicksV = config.playerTicksPerCellV ?? board.params.playerSpeedTicksPerCell.v;
  state.lantern = { fill: 0, charges: board.params.lanternStartCharges, chargeFill: 0 };
  state.beam = null;
  resetPlayer(state);
  loadObjectives(state, board, events);
  state.phase = 'playing';
  state.phaseTimer = 0;
}

function resetPlayer(state: SimState): void {
  const p = state.player;
  Object.assign(p, makePlayer(state.playerStart.x, state.playerStart.y));
  p.invincible = DEFAULTS.invincibilityTicks;
}

// ---------------------------------------------------------------------------
// Kit and lantern (Systems Brief §2, WP6)

/** A collected gem fills the lantern; once full, every params.gemsPerCharge further gems bank a charge (five make a Beam). */
function lanternGem(state: SimState, events: SimEvent[]): void {
  const l = state.lantern;
  if (l.fill < DEFAULTS.lanternGemsPerShot) {
    l.fill++;
    const full = l.fill >= DEFAULTS.lanternGemsPerShot;
    events.push({ type: 'lanternFill', fill: l.fill, full });
    if (full) events.push({ type: 'flameReady' });
  } else if (l.charges < DEFAULTS.lanternMaxCharges) {
    l.chargeFill++;
    if (l.chargeFill >= state.params.gemsPerCharge) {
      l.chargeFill = 0;
      l.charges++;
      events.push({ type: 'chargeBanked', charges: l.charges });
    }
  }
}

/** Activate a power-up now (picked before the level or dug up in it). */
export function applyKit(state: SimState, kind: KitKind, source: 'pick' | 'pickup', events: SimEvent[]): void {
  const p = state.player;
  switch (kind) {
    case 'shield':
      p.shield = DEFAULTS.shieldTicks;
      break;
    case 'turbo':
      p.turbo = DEFAULTS.turboTicks;
      break;
    case 'beam':
      state.lantern.charges = DEFAULTS.lanternMaxCharges;
      break;
    case 'fruit':
      state.bonusActive = true;
      state.bonusTimer = state.params.bonusTicks;
      state.bonusEatValue = DEFAULTS.bonusEatBasePoints;
      events.push({ type: 'bonusStart' });
      break;
    default:
      return;
  }
  events.push({ type: 'kitApplied', kind, source });
}

function tryBeam(state: SimState, events: SimEvent[]): void {
  const p = state.player;
  const l = state.lantern;
  if (l.charges < DEFAULTS.lanternChargesPerBeam || p.facing === 0) {
    events.push({ type: 'beamBlocked', charges: l.charges });
    return;
  }
  l.charges -= DEFAULTS.lanternChargesPerBeam;
  const d = p.facing;
  let x = p.x;
  let y = p.y;
  let length = 0;
  let kills = 0;
  // Screen-length: sweeps every cell to the edge, through dirt, without digging.
  for (;;) {
    x += DX[d];
    y += DY[d];
    if (!inBounds(state, x, y)) break;
    length++;
    for (const c of state.creatures.slice()) {
      const [cx, cy] = nearestCell(c);
      if (cx === x && cy === y) {
        killCreature(state, c, 'beam', DEFAULTS.flameKillPoints, events);
        kills++;
      }
    }
    const pillar = pillarAt(state, x, y);
    if (pillar) {
      // Pillars take two beams whatever their flame hits.
      const dmg = Math.ceil(pillar.hits / DEFAULTS.beamPillarHits);
      pillar.hitsLeft -= dmg - 1;
      hitPillar(state, pillar, 'flame', events);
    }
    const s = sackAt(state, x, y);
    if (s && s.kind === 'sack' && s.state !== 'fall') {
      state.sacks.splice(state.sacks.indexOf(s), 1);
      dig(state, s.x, s.y, events);
      state.golds.push({ x: s.x, y: s.y, timer: state.params.goldTicks });
      events.push({ type: 'sackBreak', x: s.x, y: s.y });
    }
  }
  state.beam = { x: p.x, y: p.y, dir: d, ticks: DEFAULTS.beamTicks, length };
  events.push({ type: 'beam', x: p.x, y: p.y, dir: d, length, kills });
}

// ---------------------------------------------------------------------------
// Helpers

function idx(state: SimState, x: number, y: number): number {
  return y * state.width + x;
}
function inBounds(state: SimState, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < state.width && y < state.height;
}
function isDug(state: SimState, x: number, y: number): boolean {
  return inBounds(state, x, y) && state.dug[idx(state, x, y)] === 1;
}
function sackAt(state: SimState, x: number, y: number): Sack | undefined {
  return state.sacks.find((s) => s.x === x && s.y === y);
}
function creatureOccupies(c: Creature, x: number, y: number): boolean {
  if (c.x === x && c.y === y) return true;
  if (c.moving && c.x + DX[c.dir] === x && c.y + DY[c.dir] === y) return true;
  return false;
}
function creatureAt(state: SimState, x: number, y: number, except?: Creature): Creature | undefined {
  return state.creatures.find((c) => c !== except && creatureOccupies(c, x, y));
}

/** Interpolated position of a mover in cell units (exact rational at tick boundaries). */
export function moverPos(m: { x: number; y: number; dir: Dir; moving: boolean; prog: number; ticks: number }, alpha = 0): [number, number] {
  if (!m.moving) return [m.x, m.y];
  const f = Math.min(1, (m.prog + alpha) / m.ticks);
  return [m.x + DX[m.dir] * f, m.y + DY[m.dir] * f];
}
function nearestCell(m: { x: number; y: number; dir: Dir; moving: boolean; prog: number; ticks: number }): [number, number] {
  const [fx, fy] = moverPos(m);
  return [Math.round(fx), Math.round(fy)];
}

function dig(state: SimState, x: number, y: number, events: SimEvent[]): void {
  const i = idx(state, x, y);
  if (state.dug[i] === 1) return;
  state.dug[i] = 1;
  state.hardDirt[i] = 0;
  onCellDug(state, x, y, events);
}

function addScore(state: SimState, n: number, events: SimEvent[]): void {
  state.score += n;
  while (state.score >= state.nextLifeAt) {
    state.nextLifeAt += DEFAULTS.extraLifeEvery;
    if (state.lives < DEFAULTS.maxSpareLives) {
      state.lives++;
      events.push({ type: 'extraLife' });
    }
  }
}

// ---------------------------------------------------------------------------
// Step

export function step(state: SimState, input: InputFrame, config: SimConfig): SimEvent[] {
  const events: SimEvent[] = [];
  state.tick++;

  switch (state.phase) {
    case 'gameOver':
    case 'won':
    case 'timeUp':
      return events;
    case 'levelClear':
      if (--state.phaseTimer <= 0) advanceLevel(state, config, events);
      return events;
    case 'dying': {
      state.phaseTimer--;
      const elapsed = DEFAULTS.deathTicks - state.phaseTimer;
      const skip = (input.dir !== 0 || input.stop || input.fire || !!input.skip) && elapsed >= DEFAULTS.deathSkipAfterTicks;
      if (state.phaseTimer <= 0 || skip) finishDeath(state, config, events);
      return events;
    }
    case 'playing':
      break;
  }

  if (config.timeLimitTicks !== undefined && state.tick >= config.timeLimitTicks) {
    state.phase = 'timeUp';
    events.push({ type: 'timeUp' });
    return events;
  }

  // Timers tick before the things that (re)start them, so a fresh timer is not consumed on its first tick.
  updateBonus(state, events);
  updateGold(state);
  applyInput(state, input, events);
  updatePlayer(state, events);
  updateFlame(state, events);
  updateSacks(state, events);
  updateSpawning(state, events);
  updateCreatures(state, events);
  checkContacts(state, events);
  const victory = state.phase === 'playing' && updateObjectives(state, events);
  checkLevelEnd(state, events, victory);
  if (state.phase === 'playing') {
    const stuck = impossibleGoal(state);
    if (stuck) failLevel(state, config, stuck, events);
  }
  return events;
}

// ---------------------------------------------------------------------------
// Input

function applyInput(state: SimState, input: InputFrame, events: SimEvent[]): void {
  const p = state.player;
  if (!p.alive) return;
  if (input.stop) {
    if (p.queued !== 0) {
      events.push({ type: 'turnMissed', dir: p.queued, reason: 'stopped', boundariesWaited: p.queuedBoundaries });
      p.queued = 0;
    }
    // Finish the current cell, then stop (playerBoundary clears the direction on arrival).
    if (p.moving) p.stopping = true;
    else p.dir = 0;
    events.push({ type: 'stop' });
  }
  if (input.dir !== 0) {
    const d = input.dir;
    if (p.moving && d === p.dir) {
      // Already going that way: nothing to queue.
    } else {
      if (p.queued !== 0) {
        events.push({ type: 'turnMissed', dir: p.queued, reason: 'replaced', boundariesWaited: p.queuedBoundaries });
      }
      p.queued = d;
      p.queuedBoundaries = 0;
      events.push({ type: 'turnQueued', dir: d });
    }
  }
  if (input.beam) tryBeam(state, events);
  else if (input.fire) tryFire(state, events);
}

/** A full lantern is a shot, a banked charge is a shot; otherwise a dry click (no cooldown, nothing fires). */
function tryFire(state: SimState, events: SimEvent[]): void {
  const p = state.player;
  if (state.flame) {
    events.push({ type: 'fireBlocked', reason: 'inFlight' });
    return;
  }
  if (p.facing === 0) {
    events.push({ type: 'fireBlocked', reason: 'noFacing' });
    return;
  }
  const l = state.lantern;
  if (l.fill >= DEFAULTS.lanternGemsPerShot) l.fill = 0;
  else if (l.charges > 0) l.charges--;
  else {
    events.push({ type: 'fireBlocked', reason: 'dry' });
    return;
  }
  state.flame = { x: p.x, y: p.y, dir: p.facing, prog: 0 };
  events.push({ type: 'fire', x: p.x, y: p.y, dir: p.facing });
}

// ---------------------------------------------------------------------------
// Player

function updatePlayer(state: SimState, events: SimEvent[]): void {
  const p = state.player;
  if (p.invincible > 0) p.invincible--;
  if (state.beam && --state.beam.ticks <= 0) state.beam = null;
  if (p.shield > 0 && --p.shield === 0) events.push({ type: 'shieldPop', cause: 'timeout' });
  if (p.turbo > 0 && --p.turbo === 0) events.push({ type: 'turboEnd' });
  if (state.chainTimer > 0 && --state.chainTimer === 0) state.chainCount = 0;
  if (!p.alive) return;

  if (p.moving) {
    p.prog++;
    if (p.prog >= p.ticks) {
      p.x += DX[p.dir];
      p.y += DY[p.dir];
      p.prog = 0;
      p.moving = false;
      arrive(state, events);
      if (state.phase !== 'playing') return;
      playerBoundary(state, events);
    }
  } else {
    playerBoundary(state, events);
  }
}

function arrive(state: SimState, events: SimEvent[]): void {
  const p = state.player;
  const i = idx(state, p.x, p.y);
  collectPickup(state, p.x, p.y, events);
  onPlayerArrive(state, p.x, p.y, events);
  if (state.gems[i] && !gemLocked(state, p.x, p.y)) {
    state.gems[i] = 0;
    state.gemsRemaining--;
    markLockedGemCollected(state, p.x, p.y);
    onGemCollected(state, p.x, p.y, state.gemTypes[i], events);
    lanternGem(state, events);
    if (state.chainTimer <= 0) state.chainCount = 0;
    state.chainCount++;
    state.chainTimer = DEFAULTS.chainTicks;
    addScore(state, DEFAULTS.gemPoints, events);
    events.push({ type: 'gem', x: p.x, y: p.y, chain: state.chainCount });
    if (state.chainCount >= DEFAULTS.octaveLength) {
      addScore(state, DEFAULTS.octavePoints, events);
      events.push({ type: 'octave' });
      state.chainCount = 0;
      state.chainTimer = 0;
    }
  }
  const gi = state.golds.findIndex((g) => g.x === p.x && g.y === p.y);
  if (gi >= 0) {
    state.golds.splice(gi, 1);
    addScore(state, DEFAULTS.goldPoints, events);
    events.push({ type: 'gold', x: p.x, y: p.y });
  }
  if (state.bonusItem && state.bonusItem.x === p.x && state.bonusItem.y === p.y) {
    state.bonusItem = null;
    addScore(state, DEFAULTS.bonusItemPoints, events);
    state.bonusActive = true;
    state.bonusTimer = state.params.bonusTicks;
    state.bonusEatValue = DEFAULTS.bonusEatBasePoints;
    events.push({ type: 'bonusStart' });
  }
}

/** Called whenever the player is exactly on a cell. Decides the next move. */
function playerBoundary(state: SimState, events: SimEvent[]): void {
  const p = state.player;
  if (p.digHold > 0) {
    // Cracking hard dirt: held at the boundary for one cell-time, then the second dig clears it.
    p.digHold--;
    return;
  }
  if (p.stopping) {
    p.stopping = false;
    p.dir = 0;
  }
  if (p.queued !== 0) {
    if (canPlayerEnter(state, p.queued)) {
      events.push({ type: 'turnExecuted', dir: p.queued, boundariesWaited: p.queuedBoundaries });
      p.dir = p.queued;
      p.queued = 0;
      p.queuedBoundaries = 0;
    } else {
      p.queuedBoundaries++;
    }
  }
  if (p.dir === 0) return;
  p.facing = p.dir;
  if (canPlayerEnter(state, p.dir)) {
    const nx = p.x + DX[p.dir];
    const ny = p.y + DY[p.dir];
    // Turbo clears hard dirt in one dig (no crack-and-hold).
    if (isHardDirt(state, nx, ny) && p.turbo > 0) digHardDirt(state, nx, ny, 'dig', events, true);
    if (isHardDirt(state, nx, ny) && !digHardDirt(state, nx, ny, 'dig', events)) {
      p.digHold = isHorizontal(p.dir) ? state.playerTicksH : state.playerTicksV;
      events.push({ type: 'dig', x: nx, y: ny, dir: p.dir });
      return;
    }
    startPlayerMove(state, p.dir, events);
    return;
  }
  // Blocked. If blocked by a sack push that failed, reverse (brief: "blocked push reverses").
  const nx = p.x + DX[p.dir];
  const ny = p.y + DY[p.dir];
  if (isHorizontal(p.dir) && sackAt(state, nx, ny)) {
    events.push({ type: 'pushBlocked', x: nx, y: ny });
    const back = OPPOSITE[p.dir];
    if (canPlayerEnter(state, back)) {
      p.dir = back;
      p.facing = back;
      startPlayerMove(state, back, events);
      return;
    }
  }
  p.dir = 0;
}

function canPlayerEnter(state: SimState, d: Dir): boolean {
  const p = state.player;
  const nx = p.x + DX[d];
  const ny = p.y + DY[d];
  if (!inBounds(state, nx, ny)) return false;
  if (pillarAt(state, nx, ny)) return false;
  const s = sackAt(state, nx, ny);
  if (s) {
    if (!isHorizontal(d)) return false;
    return canPush(state, nx, ny, DX[d]);
  }
  return true;
}

function startPlayerMove(state: SimState, d: Dir, events: SimEvent[]): void {
  const p = state.player;
  const nx = p.x + DX[d];
  const ny = p.y + DY[d];
  if (sackAt(state, nx, ny)) doPush(state, nx, ny, DX[d], events);
  if (!isDug(state, nx, ny)) {
    digHardDirt(state, nx, ny, 'dig', events); // cracked hard dirt clears on this second dig
    events.push({ type: 'dig', x: nx, y: ny, dir: d });
  }
  dig(state, nx, ny, events);
  p.dir = d;
  p.moving = true;
  p.prog = 0;
  p.ticks = isHorizontal(d) ? state.playerTicksH : state.playerTicksV;
  if (p.turbo > 0) p.ticks = Math.max(1, Math.ceil(p.ticks / DEFAULTS.turboSpeedFactor));
}

// ---------------------------------------------------------------------------
// Sack pushing (chains through adjacent sacks; far side must be open)

function pushChain(state: SimState, x: number, y: number, dx: number): Sack[] | null {
  const chain: Sack[] = [];
  let cx = x;
  for (;;) {
    const s = sackAt(state, cx, y);
    if (!s) break;
    if (s.state === 'fall') return null;
    chain.push(s);
    cx += dx;
  }
  if (!inBounds(state, cx, y)) return null;
  if (!isDug(state, cx, y)) return null; // far side must be open tunnel
  if (pillarAt(state, cx, y)) return null;
  if (creatureAt(state, cx, y)) return null;
  return chain;
}

function canPush(state: SimState, x: number, y: number, dx: number): boolean {
  return pushChain(state, x, y, dx) !== null;
}

function doPush(state: SimState, x: number, y: number, dx: number, events: SimEvent[]): void {
  const chain = pushChain(state, x, y, dx);
  if (!chain) return;
  for (const s of chain) {
    dig(state, s.x, s.y, events);
    s.slideFromX = s.x;
    s.slideT = state.playerTicksH;
    s.x += dx;
    if (s.state === 'wobble') {
      s.state = 'rest';
      s.wobble = 0;
    }
  }
  events.push({ type: 'sackPush', x, y, dx, count: chain.length });
}

// ---------------------------------------------------------------------------
// Flame

function updateFlame(state: SimState, events: SimEvent[]): void {
  const f = state.flame;
  if (!f) return;
  if (flameHits(state, f, events)) return;
  f.prog++;
  if (f.prog >= DEFAULTS.flameTicksPerCell) {
    f.prog = 0;
    const nx = f.x + DX[f.dir];
    const ny = f.y + DY[f.dir];
    const pillar = pillarAt(state, nx, ny);
    if (pillar) hitPillar(state, pillar, 'flame', events);
    if (pillar || !isDug(state, nx, ny) || sackAt(state, nx, ny)) {
      events.push({ type: 'flameEnd', x: f.x, y: f.y, dir: f.dir });
      state.flame = null;
      return;
    }
    f.x = nx;
    f.y = ny;
    flameHits(state, f, events);
  }
}

function flameHits(state: SimState, f: { x: number; y: number }, events: SimEvent[]): boolean {
  for (const c of state.creatures) {
    const [cx, cy] = nearestCell(c);
    if (cx === f.x && cy === f.y) {
      killCreature(state, c, 'flame', DEFAULTS.flameKillPoints, events);
      state.flame = null;
      return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Sacks

function sackSupported(state: SimState, s: Sack): boolean {
  if (s.y >= state.height - 1) return true;
  if (!isDug(state, s.x, s.y + 1)) return true;
  if (pillarAt(state, s.x, s.y + 1)) return true;
  const below = sackAt(state, s.x, s.y + 1);
  if (below && below.state !== 'fall') return true;
  return false;
}

function updateSacks(state: SimState, events: SimEvent[]): void {
  // Lower sacks first so stacks settle bottom-up.
  const sacks = state.sacks.slice().sort((a, b) => b.y - a.y || a.id - b.id);
  for (const s of sacks) {
    if (s.slideT > 0) s.slideT--;
    if (s.state === 'fall') {
      if (s.shadow > 0) {
        // Holding above the player's cell (sack shadow warning). Move on the tick it expires.
        s.shadow--;
        if (s.shadow > 0) continue;
      } else {
        s.fallProg++;
        if (s.fallProg < DEFAULTS.sackFallTicksPerCell) continue;
        if (!s.shadowSpent && state.params.sackShadowTicks > 0 && s.rowsFallen >= 1 && playerAtCell(state, s.x, s.y + 1)) {
          s.shadowSpent = true;
          s.shadow = state.params.sackShadowTicks;
          events.push({ type: 'sackShadow', x: s.x, y: s.y + 1, ticks: s.shadow });
          continue;
        }
      }
      s.fallProg = 0;
      const warned = s.shadowSpent;
      s.shadowSpent = false;
      dig(state, s.x, s.y, events);
      s.y++;
      s.rowsFallen++;
      crushAt(state, s.x, s.y, events, warned);
      if (state.phase !== 'playing') return;
      // A sack that has fallen ≥2 rows smashes the pillar under it and keeps falling.
      const pillarBelow = pillarAt(state, s.x, s.y + 1);
      if (pillarBelow && s.rowsFallen >= 2) hitPillar(state, pillarBelow, 'sack', events);
      if (sackSupported(state, s)) {
        events.push({ type: 'sackLand', x: s.x, y: s.y, rows: s.rowsFallen });
        if (s.rowsFallen > DEFAULTS.sackBreakRows && s.kind === 'sack') {
          state.sacks.splice(state.sacks.indexOf(s), 1);
          dig(state, s.x, s.y, events);
          state.golds.push({ x: s.x, y: s.y, timer: state.params.goldTicks });
          events.push({ type: 'sackBreak', x: s.x, y: s.y });
        } else {
          s.state = 'rest';
          s.rowsFallen = 0;
          s.wobble = 0;
        }
      }
      continue;
    }
    if (sackSupported(state, s)) {
      if (s.state === 'wobble') {
        s.state = 'rest';
        s.wobble = 0;
      }
      continue;
    }
    if (s.state === 'rest') {
      s.state = 'wobble';
      s.wobble = 0;
      events.push({ type: 'wobbleStart', x: s.x, y: s.y });
    }
    s.wobble++;
    if (s.wobble >= DEFAULTS.sackWobbleTicks) {
      s.state = 'fall';
      s.fallProg = 0;
      s.rowsFallen = 0;
      events.push({ type: 'sackFall', x: s.x, y: s.y });
      // Creatures in this column change direction.
      for (const c of state.creatures) {
        const [cx] = nearestCell(c);
        if (cx === s.x && c.dir !== 0) {
          c.dir = OPPOSITE[c.dir];
          if (c.moving) {
            // Reverse mid-cell: swap origin/target so interpolation stays continuous.
            c.x += DX[OPPOSITE[c.dir]];
            c.y += DY[OPPOSITE[c.dir]];
            c.prog = c.ticks - c.prog;
          }
        }
      }
    }
  }
}

/** Player occupancy for the sack-shadow check (nearest cell, like every other crush). */
function playerAtCell(state: SimState, x: number, y: number): boolean {
  const p = state.player;
  if (!p.alive) return false;
  const [px, py] = nearestCell(p);
  return px === x && py === y;
}

/**
 * A warned crush (after a sack shadow) treats a moving player as already in the cell they are
 * heading to, so reacting to the warning by swiping away is enough to escape.
 */
function playerCommittedCell(p: Player): [number, number] {
  if (p.moving) return [p.x + DX[p.dir], p.y + DY[p.dir]];
  return [p.x, p.y];
}

function crushAt(state: SimState, x: number, y: number, events: SimEvent[], warned = false): void {
  for (const c of state.creatures.slice()) {
    const [cx, cy] = nearestCell(c);
    if (cx === x && cy === y) {
      killCreature(state, c, 'sack', DEFAULTS.crushKillPoints, events);
      onCreatureCrushed(state, x, y, events);
    }
  }
  const p = state.player;
  if (p.alive) {
    const [px, py] = warned ? playerCommittedCell(p) : nearestCell(p);
    if (px === x && py === y && !killPlayer(state, 'crushed', events)) {
      // Shield took the crush: the sack bursts into gold on the spot.
      const s = sackAt(state, x, y);
      if (s && s.kind === 'sack') {
        state.sacks.splice(state.sacks.indexOf(s), 1);
        state.golds.push({ x, y, timer: state.params.goldTicks });
        events.push({ type: 'sackBreak', x, y });
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Gold

function updateGold(state: SimState): void {
  for (let i = state.golds.length - 1; i >= 0; i--) {
    if (--state.golds[i].timer <= 0) state.golds.splice(i, 1);
  }
}

// ---------------------------------------------------------------------------
// Creatures

function updateSpawning(state: SimState, events: SimEvent[]): void {
  if (state.spawned >= state.total) return;
  if (state.spawnTimer > 0) {
    state.spawnTimer--;
    return;
  }
  if (state.creatures.length >= state.params.onScreenCap) return;
  const { cell: at, nest } = nextSpawnPoint(state);
  if (creatureAt(state, at.x, at.y)) return;
  const c: Creature = {
    id: state.nextCreatureId++,
    x: at.x,
    y: at.y,
    dir: 0,
    moving: false,
    prog: 0,
    ticks: state.params.creatureTicksPerCell,
    kind: 'crawler',
    frustration: 0,
    turnPenalty: 0,
  };
  state.creatures.push(c);
  state.spawned++;
  state.spawnTimer = nest && nest.spawnGap > 0 ? nest.spawnGap : state.params.spawnGapTicks;
  const last = state.spawned === state.total;
  events.push({ type: 'creatureSpawn', id: c.id, last, x: at.x, y: at.y });
  if (last && !state.bonusItem && !state.bonusActive) {
    state.bonusItem = { x: state.spawn.x, y: state.spawn.y };
    events.push({ type: 'bonusAppear', x: state.spawn.x, y: state.spawn.y });
  }
}

/** Rank the four directions: axis with the larger distance first; flee inverts. */
function rankDirs(state: SimState, c: Creature): Dir[] {
  const p = state.player;
  let dx = p.x - c.x;
  let dy = p.y - c.y;
  // Bonus mode and Turbo both invert the chase: creatures steer away from Pip.
  if (state.bonusActive || p.turbo > 0) {
    dx = -dx;
    dy = -dy;
  }
  const h: Dir = dx > 0 ? 2 : dx < 0 ? 4 : c.x < state.width / 2 ? 2 : 4;
  const v: Dir = dy > 0 ? 3 : dy < 0 ? 1 : c.y < state.height / 2 ? 3 : 1;
  if (Math.abs(dy) > Math.abs(dx)) return [v, h, OPPOSITE[h], OPPOSITE[v]];
  return [h, v, OPPOSITE[v], OPPOSITE[h]];
}

function crawlerCanEnter(state: SimState, c: Creature, x: number, y: number): 'ok' | 'dirt' | 'sack' | 'creature' | 'oob' {
  if (!inBounds(state, x, y)) return 'oob';
  if (sackAt(state, x, y) || pillarAt(state, x, y)) return 'sack';
  if (creatureAt(state, x, y, c)) return 'creature';
  if (!isDug(state, x, y)) return 'dirt';
  return 'ok';
}

function updateCreatures(state: SimState, events: SimEvent[]): void {
  for (const c of state.creatures.slice()) {
    if (!state.creatures.includes(c)) continue;
    if (c.kind === 'borer') {
      c.frustration++;
      if (c.frustration > state.params.mutateDurationTicks) {
        c.kind = 'crawler';
        c.frustration = 0;
        events.push({ type: 'mutate', id: c.id, kind: 'crawler' });
      }
    }
    if (c.moving) {
      c.prog++;
      if (c.prog >= c.ticks) {
        c.x += DX[c.dir];
        c.y += DY[c.dir];
        c.prog = 0;
        c.moving = false;
        creatureBoundary(state, c, events);
      }
    } else {
      creatureBoundary(state, c, events);
    }
  }
}

function creatureBoundary(state: SimState, c: Creature, events: SimEvent[]): void {
  if (c.turnPenalty > 0) {
    c.turnPenalty--;
    return;
  }
  const ranked = rankDirs(state, c);
  const rev = c.dir === 0 ? 0 : OPPOSITE[c.dir];
  let order = ranked.filter((d) => d !== rev);
  if (c.kind === 'crawler' && state.params.turnSwapChance > 0 && order.length >= 3) {
    if (rngNext(state) < state.params.turnSwapChance) {
      order = [order[2], order[1], order[0], ...order.slice(3)];
    }
  }
  if (rev !== 0) order.push(rev);

  let chosen: Dir = 0;
  if (c.kind === 'borer') {
    // Borer ignores tunnels: takes its preferred direction, only creatures block it.
    for (const d of order) {
      const nx = c.x + DX[d];
      const ny = c.y + DY[d];
      if (!inBounds(state, nx, ny)) continue;
      if (creatureAt(state, nx, ny, c)) continue;
      // Pillars, boulders and locked gem cases are Borer-proof: it picks another direction.
      if (pillarAt(state, nx, ny) || sackAt(state, nx, ny)?.kind === 'boulder' || gemLocked(state, nx, ny)) continue;
      chosen = d;
      break;
    }
  } else {
    let first = true;
    for (const d of order) {
      const r = crawlerCanEnter(state, c, c.x + DX[d], c.y + DY[d]);
      if (first && (r === 'sack' || r === 'creature' || (r === 'dirt' && state.params.frustrationCountsDirt))) c.frustration++;
      first = false;
      if (r === 'ok') {
        chosen = d;
        break;
      }
    }
    if (chosen === 0) c.frustration++;
    if (c.frustration > state.params.mutateThreshold) {
      c.kind = 'borer';
      c.frustration = 0;
      events.push({ type: 'mutate', id: c.id, kind: 'borer' });
      return; // acts as a Borer from the next tick
    }
  }
  if (chosen === 0) {
    c.moving = false;
    return;
  }
  if (c.dir !== 0 && chosen !== c.dir && DEFAULTS.turnPenaltyTicks > 0) {
    c.dir = chosen;
    c.turnPenalty = DEFAULTS.turnPenaltyTicks;
    c.moving = false;
    events.push({ type: 'creatureTurn', id: c.id, dir: chosen });
    return;
  }
  startCreatureMove(state, c, chosen, events);
}

function startCreatureMove(state: SimState, c: Creature, d: Dir, events: SimEvent[]): void {
  const nx = c.x + DX[d];
  const ny = c.y + DY[d];
  if (c.kind === 'borer') {
    // Digs through anything, eats gems and sacks in its path.
    const i = idx(state, nx, ny);
    if (state.gems[i]) {
      state.gems[i] = 0;
      state.gemsRemaining--;
    }
    const s = sackAt(state, nx, ny);
    if (s && s.kind === 'sack') {
      state.sacks.splice(state.sacks.indexOf(s), 1);
      events.push({ type: 'sackBreak', x: nx, y: ny });
    }
    if (!isDug(state, nx, ny)) digHardDirt(state, nx, ny, 'borer', events);
    dig(state, nx, ny, events);
  }
  c.dir = d;
  c.moving = true;
  c.prog = 0;
  c.ticks = state.params.creatureTicksPerCell;
}

function killCreature(state: SimState, c: Creature, by: 'flame' | 'sack' | 'eaten' | 'beam' | 'shield', points: number, events: SimEvent[]): void {
  const i = state.creatures.indexOf(c);
  if (i < 0) return;
  state.creatures.splice(i, 1);
  state.killed++;
  addScore(state, points, events);
  events.push({ type: 'creatureKill', id: c.id, by, points });
}

// ---------------------------------------------------------------------------
// Bonus

function updateBonus(state: SimState, events: SimEvent[]): void {
  if (!state.bonusActive) return;
  if (--state.bonusTimer <= 0) {
    state.bonusActive = false;
    events.push({ type: 'bonusEnd' });
  }
}

// ---------------------------------------------------------------------------
// Contacts, death, level end

function checkContacts(state: SimState, events: SimEvent[]): void {
  const p = state.player;
  if (!p.alive) return;
  const [px, py] = moverPos(p);
  for (const c of state.creatures.slice()) {
    const [cx, cy] = moverPos(c);
    if (Math.abs(cx - px) < DEFAULTS.contactDistance && Math.abs(cy - py) < DEFAULTS.contactDistance) {
      if (state.bonusActive) {
        killCreature(state, c, 'eaten', state.bonusEatValue, events);
        state.bonusEatValue *= 2;
        state.total++; // replacement keeps coming
      } else if (p.invincible <= 0) {
        if (!killPlayer(state, 'creature', events)) {
          // Shield absorbed the contact: the creature is knocked out, no points.
          killCreature(state, c, 'shield', 0, events);
          continue;
        }
        return;
      }
    }
  }
}

/** Returns false when the shield absorbed the hit (Pip lives, bubble pops). */
function killPlayer(state: SimState, cause: 'creature' | 'crushed', events: SimEvent[]): boolean {
  const p = state.player;
  if (!p.alive) return true;
  if (p.shield > 0) {
    p.shield = 0;
    p.invincible = Math.max(p.invincible, DEFAULTS.invincibilityTicks);
    events.push({ type: 'shieldPop', cause });
    return false;
  }
  p.alive = false;
  p.moving = false;
  p.deathCause = cause;
  if (p.queued !== 0) {
    events.push({ type: 'turnMissed', dir: p.queued, reason: 'death', boundariesWaited: p.queuedBoundaries });
    p.queued = 0;
  }
  state.flame = null;
  state.phase = 'dying';
  state.phaseTimer = DEFAULTS.deathTicks;
  onPlayerDeath(state);
  p.shield = 0;
  p.turbo = 0;
  events.push({ type: 'playerDeath', cause });
  return true;
}

function finishDeath(state: SimState, config: SimConfig, events: SimEvent[]): void {
  if (!config.infiniteLives) {
    if (state.lives <= 0) {
      state.phase = 'gameOver';
      events.push({ type: 'gameOver' });
      return;
    }
    state.lives--;
  }
  // Creatures alive at death go back into the spawn queue (B1, 2026-09-07): before this they simply
  // vanished, so a couple of deaths could exhaust the creature total and leave a crush goal
  // impossible (the "all gems collected, level didn't end" report).
  state.spawned -= state.creatures.length;
  state.creatures = [];
  state.flame = null;
  state.spawnTimer = state.params.spawnGapTicks;
  state.bonusActive = false;
  state.bonusTimer = 0;
  state.chainCount = 0;
  state.chainTimer = 0;
  for (const s of state.sacks) s.slideT = 0;
  resetPlayer(state);
  state.phase = 'playing';
  events.push({ type: 'respawn' });
}

/**
 * Backstop for goals that can no longer complete (B1): the level restarts at the cost of a life
 * instead of soft-locking. Runtime cases are creature-bound (crush with the creature total spent
 * and none alive); the static cases (counts vs the board) are rejected at load in board.ts.
 */
function failLevel(state: SimState, config: SimConfig, goal: Goal, events: SimEvent[]): void {
  const p = state.player;
  if (p.queued !== 0) {
    events.push({ type: 'turnMissed', dir: p.queued, reason: 'levelEnd', boundariesWaited: p.queuedBoundaries });
    p.queued = 0;
  }
  events.push({ type: 'goalImpossible', goalId: goal.id, goalType: goal.type });
  if (!config.infiniteLives) {
    if (state.lives <= 0) {
      state.phase = 'gameOver';
      events.push({ type: 'gameOver' });
      return;
    }
    state.lives--;
  }
  loadLevel(state, config.boards[state.boardIndex], config, events);
}

function checkLevelEnd(state: SimState, events: SimEvent[], victory: boolean): void {
  if (state.phase !== 'playing') return;
  // Boards with goals end on the final phase's goals; the legacy rule (all gems or all creatures)
  // applies only to boards without goals, and each condition only when the board has that resource.
  const allGems = state.gemsTotal > 0 && state.gemsRemaining <= 0;
  const allDead = state.total > 0 && state.killed >= state.total && state.creatures.length === 0;
  const done = hasGoals(state) ? victory : allGems || allDead;
  if (done) {
    const p = state.player;
    if (p.queued !== 0) {
      events.push({ type: 'turnMissed', dir: p.queued, reason: 'levelEnd', boundariesWaited: p.queuedBoundaries });
      p.queued = 0;
    }
    state.phase = 'levelClear';
    state.phaseTimer = DEFAULTS.levelClearTicks;
    state.levelsCleared++;
    events.push({ type: 'levelClear', level: state.level, boardId: state.boardId });
  }
}

function advanceLevel(state: SimState, config: SimConfig, events: SimEvent[]): void {
  let next = state.boardIndex + 1;
  if (next >= config.boards.length) {
    if (config.mode === 'campaign') {
      state.phase = 'won';
      events.push({ type: 'won' });
      return;
    }
    next = 0;
  }
  state.boardIndex = next;
  state.level++;
  const loadEvents: SimEvent[] = [];
  loadLevel(state, config.boards[next], config, loadEvents);
  events.push({ type: 'levelStart', level: state.level, boardId: state.boardId }, ...loadEvents);
}
